import { SavedPairCriteria } from '@aloft/toolkit-shared';
import { Block, Button, Drawer, Flex, Icon, Input, Tooltip, Typography } from '@aloft/toolkit-sui';

import { Controller } from 'react-hook-form';

import {
  AreaRangeInputs,
  BooleanSelect,
  DateRangeInputs,
  HeaderRow,
  MinMaxInputs,
  PriceRangeInputs,
  Row,
  StringSelect,
} from '../../../../components/FilterDrawer/index.ts';
import { Table, Tbody, Td, Thead } from '../../../../components/table.tsx';
import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { MatchCriteria } from '../../matching/types.ts';
import { PairedSalesFormSchema } from '../../types.ts';
import { savedCriteriaToWorkerInput } from '../../utils/savedCriteriaToWorkerInput.ts';
import { DefineTheDifference } from './DefineTheDifference.tsx';
import { DefineTheMatchInputs } from './DefineTheMatchInputs.tsx';
import { getEnumOptions } from './getEnumOptions.ts';
import { getErrorMessages } from './getErrorMessages.ts';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onApply: () => void;
};

export const FilterDrawer = ({ isOpen, onCancel, onApply }: Props) => {
  const { control, formState, trigger, setValue, watch } =
    useZodFormContext<PairedSalesFormSchema>();

  if (!isOpen) return null;

  const formattedErrors = getErrorMessages(formState.errors);

  const pairCriteria = watch('search.pairCriteria') as SavedPairCriteria;
  const match = savedCriteriaToWorkerInput(pairCriteria).matchCriteria;
  const difference = pairCriteria.differenceCriteria;

  const setMatchCriteria = (m: MatchCriteria) => setValue('search.pairCriteria.matchCriteria', m);

  const onSubmit = async () => {
    const areFiltersValid = await trigger('search.filters');
    if (!areFiltersValid) return;

    onApply();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCancel}
      size="xl"
      placement="left"
      header={<Typography variant="h1">Filter</Typography>}
      drawerContentProps={{
        backgroundColor: 'surface2',
        paddingX: '24px',
        paddingY: '22px',
      }}
      footer={
        <Flex gap="8px" paddingTop="8px">
          <Button variant="secondary" text="Cancel" onClick={onCancel} />
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <Button text="Apply" onClick={onSubmit} />
        </Flex>
      }
      footerProps={{
        justifyContent: 'flex-start',
        paddingTop: '8px',
        paddingBottom: '0',
      }}
      variant="overlay"
      closeOnOverlayClick={false}
    >
      <Flex width="100%" height="100%" flexDirection="column" gap="24px">
        <Block>
          <Flex gap="1rem" alignItems="center">
            <Typography variant="h3">Define the difference</Typography>
            <Tooltip
              width="17rem"
              padding="0.75rem"
              label={
                <Flex flexDirection="column" gap="1rem">
                  <Block textAlign="center">
                    Use this select to define how the sales in the pair must be different from each
                    other.
                  </Block>
                  <Block textAlign="center">
                    Ex. Sale 1 must include the word pool; Sale 2 must not include the word pool.
                  </Block>
                  <Block textAlign="center">
                    Note the keyword search searches marketing commentary in the listing.
                  </Block>
                </Flex>
              }
            >
              <Flex cursor="pointer">
                <Icon icon="question" />
              </Flex>
            </Tooltip>
          </Flex>
          <DefineTheDifference
            criteria={difference}
            setCriteria={(diff) => {
              setValue('search.pairCriteria.differenceCriteria', diff);
            }}
          />
        </Block>
        <Block>
          <Typography variant="h3">Set filters & pair criteria</Typography>
          <Table>
            <Thead backgroundColor="surfaceAccent7">
              <HeaderRow includePairedSales />
            </Thead>
            <Tbody>
              {/* Shown to the user as property type, but this field internally represents property subtypes */}
              <Row label="Property Type">
                <StringSelect
                  name="search.filters.propertySubType"
                  options={getEnumOptions({
                    name: 'search.filters.propertySubType',
                  })}
                  placeholder=" "
                />
                <DefineTheMatchInputs
                  id="propertySubType"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Sale Date">
                <DateRangeInputs
                  minName="search.filters.saleDate.min"
                  maxName="search.filters.saleDate.max"
                />
                <DefineTheMatchInputs
                  id="saleDate"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Sale Price">
                <PriceRangeInputs
                  minName="search.filters.salePrice.min"
                  maxName="search.filters.salePrice.max"
                />
                <DefineTheMatchInputs
                  id="salePrice"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="GLA">
                <AreaRangeInputs
                  minName="search.filters.gla.min"
                  maxName="search.filters.gla.max"
                />
                <DefineTheMatchInputs
                  id="glaSqFt"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Lot Size">
                <AreaRangeInputs
                  minName="search.filters.lotSize.min"
                  maxName="search.filters.lotSize.max"
                />
                <DefineTheMatchInputs
                  id="siteSizeSqFt"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Proximity">
                <Td colSpan={2} />
                <DefineTheMatchInputs id="coords" criteria={match} setCriteria={setMatchCriteria} />
              </Row>
              <Row label="Zip Code">
                <Td colSpan={2}>
                  <Controller
                    name="search.filters.zipCode"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Input
                        size="xs"
                        id="zipCode"
                        type="number"
                        autoFocus
                        isInvalid={fieldState.invalid}
                        onClear={() => {
                          field.onChange('');
                        }}
                        {...field}
                      />
                    )}
                  />
                </Td>
                <DefineTheMatchInputs
                  id="zipCode"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Year Built">
                <MinMaxInputs
                  minName="search.filters.yearBuilt.min"
                  maxName="search.filters.yearBuilt.max"
                />
                <DefineTheMatchInputs
                  id="yearBuilt"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Bedrooms">
                <MinMaxInputs
                  minName="search.filters.bedrooms.min"
                  maxName="search.filters.bedrooms.max"
                />
                <DefineTheMatchInputs
                  id="bedrooms"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Baths: Full">
                <MinMaxInputs
                  minName="search.filters.fullBaths.min"
                  maxName="search.filters.fullBaths.max"
                />
                <DefineTheMatchInputs
                  id="bathrooms"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Baths: 3/4">
                <MinMaxInputs
                  minName="search.filters.threeQuarterBaths.min"
                  maxName="search.filters.threeQuarterBaths.max"
                />
                <DefineTheMatchInputs
                  id="threeQuarterBathrooms"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Baths: Half">
                <MinMaxInputs
                  minName="search.filters.halfBaths.min"
                  maxName="search.filters.halfBaths.max"
                />
                <DefineTheMatchInputs
                  id="halfBathrooms"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Baths: Total">
                <MinMaxInputs
                  minName="search.filters.totalBaths.min"
                  maxName="search.filters.totalBaths.max"
                />
                <DefineTheMatchInputs
                  id="totalBathrooms"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Stories">
                <MinMaxInputs
                  minName="search.filters.stories.min"
                  maxName="search.filters.stories.max"
                />
                <DefineTheMatchInputs
                  id="stories"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Garage Spaces">
                <MinMaxInputs
                  minName="search.filters.garageSpaces.min"
                  maxName="search.filters.garageSpaces.max"
                />
                <DefineTheMatchInputs
                  id="attachedGarageParkingSpacesCount"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Has Basement">
                <BooleanSelect name="search.filters.hasBasement" />
                <DefineTheMatchInputs
                  id="hasBasement"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
              <Row label="Attachment">
                <BooleanSelect name="search.filters.attached" />
                <DefineTheMatchInputs
                  id="hasAttachment"
                  criteria={match}
                  setCriteria={setMatchCriteria}
                />
              </Row>
            </Tbody>
          </Table>
        </Block>
        {!!formattedErrors.length && (
          <Block>
            {formattedErrors.map((error) => (
              <Typography color="error" key={error}>
                {error}
              </Typography>
            ))}
          </Block>
        )}
      </Flex>
    </Drawer>
  );
};
